import { Component, OnInit, OnDestroy } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from './services/auth.service';
import { VersionCheckService } from './services/version-check.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit, OnDestroy {
  items: MenuItem[] = [];
  rol: string = localStorage.getItem('rol') || '0';
  showMenu: boolean = false;
  updateAvailable: boolean = false;
  sidebarCollapsed: boolean = false;
  mobileOpen: boolean = false;
  expandedGroups: Set<string> = new Set();

  toggleSidebar() {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  toggleMobile() {
    this.mobileOpen = !this.mobileOpen;
  }

  closeMobile() {
    this.mobileOpen = false;
  }

  toggleGroup(label: string): void {
    if (this.expandedGroups.has(label)) {
      this.expandedGroups.delete(label);
    } else {
      this.expandedGroups.add(label);
    }
  }

  isGroupExpanded(label: string): boolean {
    return this.expandedGroups.has(label);
  }

  isChildActive(item: MenuItem): boolean {
    return (item.items || []).some(sub =>
      this.router.url.startsWith(sub.routerLink?.[0] || '__never__')
    );
  }

  private autoExpandGroups(): void {
    for (const item of this.items) {
      if (item.items && item.label) {
        const hasActive = item.items.some(sub =>
          this.router.url.startsWith(sub.routerLink?.[0] || '__never__')
        );
        if (hasActive) {
          this.expandedGroups.add(item.label);
        }
      }
    }
  }
  private subscriptions: Subscription = new Subscription();

  constructor(
    private router: Router,
    private authService: AuthService,
    private versionCheck: VersionCheckService
  ) {
    
    this.showMenu = this.authService.isAuthenticated();
    if (this.showMenu) {
      this.updateMenuItems();
    }

    // Subscribe to auth changes
    this.subscriptions.add(
      this.authService.authState$.subscribe((state) => {
        this.showMenu = state.authenticated;
        if (state.authenticated) {
          this.items = this.getMenuItems(state.role);
        } else {
          this.items = [];
        }
      })
    );

    // Subscribe to route changes
    this.subscriptions.add(
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe((event: any) => {
        
        if (!event.url.includes('/auth/')) {
          this.updateMenuItems();
          this.autoExpandGroups();
        }
        this.showMenu = !event.url.includes('/auth/') && this.authService.isAuthenticated();
        this.mobileOpen = false;
      })
    );
  }

  private updateMenuItems() {
    
    if (!this.authService.isAuthenticated()) {
      this.items = [];
      return;
    }
    const rol = localStorage.getItem('rol') || '0';
    this.items = this.getMenuItems(rol);
  }

  ngOnInit() {
    this.updateMenuItems();
    this.autoExpandGroups();
    this.versionCheck.startChecking();
    this.subscriptions.add(
      this.versionCheck.updateAvailable$.subscribe(available => {
        this.updateAvailable = available;
      })
    );
  }

  reloadPage(): void {
    window.location.reload();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  getNavColor(item: MenuItem): { bg: string; icon: string } {
    const route = item.routerLink?.[0] || '';
    const labelKey = `label:${item.label}`;
    const map: Record<string, { bg: string; icon: string }> = {
      '/dashboard/home':      { bg: '#eff6ff', icon: '#3b82f6' },
      '/contracts':           { bg: '#eff6ff', icon: '#3b82f6' },
      '/gestores':            { bg: '#eef2ff', icon: '#6366f1' },
      '/solicitudes/web':     { bg: '#fff7ed', icon: '#f97316' },
      '/pagos-sin-procesar':  { bg: '#fdf2f8', icon: '#ec4899' },
      '/tickets':             { bg: '#fefce8', icon: '#ca8a04' },
      '/usuarios':            { bg: '#f0fdf4', icon: '#22c55e' },
      '/solicitudes':         { bg: '#eff6ff', icon: '#3b82f6' },
      '/solicitudes/avales':  { bg: '#f0fdfa', icon: '#14b8a6' },
      '/precomite':           { bg: '#fffbeb', icon: '#d97706' },
      '/groups':              { bg: '#faf5ff', icon: '#a855f7' },
      '/promoteurs':          { bg: '#fff7ed', icon: '#f97316' },
      '/comite':              { bg: '#f0fdf4', icon: '#22c55e' },
      '/catalogo-comision':  { bg: '#fdf4ff', icon: '#d946ef' },
      '/asignacion-actual':  { bg: '#fff7ed', icon: '#ea580c' },
      '/agendas':            { bg: '#f0fdf4', icon: '#10b981' },
      '/cartera-renovacion': { bg: '#ecfdf5', icon: '#10b981' },
      '/agenda-visitas':     { bg: '#eff6ff', icon: '#3b82f6' },
      '/rewards':            { bg: '#fdf2f8', icon: '#ec4899' },
      '/contratos-comite':   { bg: '#f0fdf4', icon: '#22c55e' },
      '/mantenimiento':      { bg: '#f1f5f9', icon: '#64748b' },
    };
    return map[route] || map[labelKey] || { bg: '#f1f5f9', icon: '#64748b' };
  }

  private getMenuItems(rol: string): MenuItem[] {
    const menuItems: MenuItem[] = [
        {
            label: '',
            icon: 'pi pi-home',
            routerLink: ['/dashboard/home']
        }
    ];
    
    if ([1, 2, 3, 4, 5, 6].includes(+rol)) {  // Changed from +this.rol to +rol
      menuItems.push(
        {
          label: 'Contracts',
          icon: 'pi pi-folder',
          routerLink: ['/contracts']
        },
      );
    }
    if ([1, 2].includes(+rol)) {  // Changed from +this.rol to +rol
      menuItems.push(
        // {
        //   label: 'Plantillas',
        //   icon: 'pi pi-file-o',
        //   routerLink: ['/plantillas']
        // },
        {
          label: 'Gestores',
          icon: 'pi pi-folder-open',
          routerLink: ['/gestores']
        },
        {
          label: 'Solicitudes Web',
          icon: 'pi pi-desktop',
          routerLink: ['/solicitudes/web']
        }
      );
    }
    if ([1, 2, 3].includes(+rol)) {  // Changed from +this.rol to +rol
      menuItems.push(
        {
          label: 'Pagos sin procesar',
          icon: 'pi pi-arrow-right-arrow-left',
          routerLink: ['/pagos-sin-procesar']
        }
      );
      menuItems.push(
        {
          label: 'Tickets',
          icon: 'pi pi-ticket',
          routerLink: ['/tickets']
        }
      );
      menuItems.push(
        {
          label: 'Contratos Comité',
          icon: 'pi pi-file',
          routerLink: ['/contratos-comite']
        }
      );
    }
    if ([1].includes(+rol)) {  // Changed from +this.rol to +rol
      menuItems.push({
        label: 'Usuarios',
        icon: 'pi pi-user',
        routerLink: ['/usuarios']
      });
      menuItems.push({
        label: 'Comercios Toku',
        icon: 'pi pi-shopping-bag',
        routerLink: ['/comercios-toku']
      });
      menuItems.push({
        label: 'Catálogo Comisión',
        icon: 'pi pi-percentage',
        routerLink: ['/catalogo-comision']
      });
      menuItems.push({
        label: 'Rewards',
        icon: 'pi pi-gift',
        routerLink: ['/rewards']
      });
    }
    if ([1, 2].includes(+rol)) {
      menuItems.push({
        label: 'Mantenimiento',
        icon: 'pi pi-wrench',
        routerLink: ['/mantenimiento']
      });
    }

    
    if ([1, 2, 3, 4, 5, 6].includes(+rol)) {  // Changed from +this.rol to +rol
      menuItems.push(
        {
          label: 'Solicitudes',
          icon: 'pi pi-clock',
          routerLink: ['/solicitudes']
        },
        {
          label: 'Precomité',
          icon: 'pi pi-hourglass',
          routerLink: ['/precomite']
        },
        {
          label: 'Avales',
          icon: 'pi pi-id-card',
          routerLink: ['/solicitudes/avales']
        },
        // {
        //   label: 'Solicitudes',
        //   icon: 'pi pi-file',
        //   items: [
        //     {
        //       label: 'Pendientes',
        //       icon: 'pi pi-clock',
        //       routerLink: ['/solicitudes/pending']
        //     },
        //     {
        //       label: 'Procesadas',
        //       icon: 'pi pi-check-circle',
        //       routerLink: ['/solicitudes/processed']
        //     },
        //     {
        //       label: 'Incompletas',
        //       icon: 'pi pi-check-circle',
        //       routerLink: ['/solicitudes/incomplete']
        //     },
        //   ]
        // },
        {
          label: 'Grupos',
          icon: 'pi pi-sitemap',
          routerLink: ['/groups']
        },
        {
          label: 'Promoteurs',
          icon: 'pi pi-users',
          routerLink: ['/promoteurs']
        },
        {
          label: 'Comité',
          icon: 'pi pi-check-circle',
          routerLink: ['/comite']
        }
      );
    }

    if ([7].includes(+rol)) {
      menuItems.push(
        { label: 'Contracts', icon: 'pi pi-folder', routerLink: ['/contracts'] },
        { label: 'Promoteurs', icon: 'pi pi-users', routerLink: ['/promoteurs'] }
      );
    }

    if ([1, 4, 5, 6].includes(+rol)) {
      menuItems.push(
        {
          label: 'Asignación Actual',
          icon: 'pi pi-map-marker',
          routerLink: ['/asignacion-actual']
        },
        {
          label: 'Agendas',
          icon: 'pi pi-calendar-plus',
          routerLink: ['/agendas']
        }
      );
    }

    // if ([1, 2, 5].includes(+rol)) {  // Changed from +this.rol to +rol
    //   menuItems.push({
    //     label: 'Promoteurs',
    //     icon: 'pi pi-users',
    //     routerLink: ['/promoteurs']
    //   });
    // }

    return menuItems;
  }
}
